import { type NameMatch, matchName } from "src/components/commandbar/match";
import {
  type CommandResult,
  type CommandResultSource,
  type CommandScope,
  commandScopeLabel,
  scopeLabelMatchStart,
} from "src/components/commandbar/types";

type PaletteAction = {
  id: string;
  name: string;
  keywords?: string;
  parent?: string;
  priority?: number;
  section?: string | { name: string; priority?: number };
};

const sectionName = (action: PaletteAction) =>
  typeof action.section === "object"
    ? action.section.name
    : (action.section ?? "");

// kbar's empty-query order: sections by their highest priority, then actions by priority, each stable in registration order.
const inKbarOrder = <A extends PaletteAction>(actions: A[]): A[] => {
  const sections = new Map<string, { priority: number; actions: A[] }>();
  for (const action of actions) {
    const name = sectionName(action);
    const priority =
      typeof action.section === "object" ? (action.section.priority ?? 0) : 0;
    const section = sections.get(name) ?? { priority, actions: [] };
    section.priority = Math.max(section.priority, priority);
    section.actions.push(action);
    sections.set(name, section);
  }
  return [...sections.values()]
    .sort((a, b) => b.priority - a.priority)
    .flatMap((section) =>
      section.actions.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0)),
    );
};

export type PaletteRow<A extends PaletteAction> =
  | {
      kind: "action";
      key: string;
      action: A;
      match: NameMatch | null;
      // A command that opens a type's page ("Library") also searches that type on Tab.
      scope?: CommandScope;
    }
  | {
      kind: "result";
      key: string;
      result: CommandResult;
      match: NameMatch | null;
    }
  | {
      kind: "scope";
      key: string;
      scope: CommandScope;
      label: string;
      match: NameMatch;
    }
  | { kind: "agent"; key: string; query: string; match: null }
  | { kind: "error"; key: string; match: null }
  | { kind: "header"; key: string; label: string }
  | { kind: "separator"; key: string };

type BuildRowsArgs<A extends PaletteAction> = {
  search: string;
  scope: CommandScope | null;
  rootActionId: string | null | undefined;
  // kbar's `useMatches` results: section names interleaved with actions.
  matches: Array<string | A>;
  // Every registered action. Typed queries match these directly, since kbar's matches trail input by ~100ms.
  allActions: A[];
  contextActionIds: string[];
  contextObjectKey: string | null;
  sources: CommandResultSource[];
  // Whether a typed query ends with a row that asks the agent about it.
  canAskAgent: boolean;
};

const MAX_ROWS = 12;
const MAX_SCOPED_ROWS = 60;
const MAX_RECENT_ROWS = 5;
// A match on keywords alone ranks below any name match.
const KEYWORD_MATCH_SCORE = 0.5;
// Workspace search results only belong inside the "Switch workspace" page.
const NESTED_ONLY_PREFIX = "workspace.switch.";
const MIN_SCOPE_QUERY_LENGTH = 2;

const isChildOf = (match: string | PaletteAction, parent: string) =>
  typeof match !== "string" && match.parent === parent;

export const resultNavigateOptions = (
  result: Pick<CommandResult, "keepsOrigin">,
  captureOrigin: () => object | undefined,
) => {
  const state = result.keepsOrigin ? captureOrigin() : undefined;
  return state ? { state } : undefined;
};

// Any real row leads. Then the retry row, so plain Enter retries rather than asking the agent.
// While results load, the agent row leads only once picked, so an early Enter doesn't start a chat.
export const pickDefaultRow = <R extends { kind: string }>(
  selectable: R[],
  isLoading: boolean,
): R | undefined =>
  selectable.find((row) => row.kind !== "agent" && row.kind !== "error") ??
  selectable.find((row) => row.kind === "error") ??
  (isLoading ? undefined : selectable.find((row) => row.kind === "agent"));

export const resultKey = (result: Pick<CommandResult, "type" | "id">) =>
  `${result.type}:${result.id}`;

export const isSelectableRow = <A extends PaletteAction>(
  row: PaletteRow<A>,
): row is Extract<
  PaletteRow<A>,
  { kind: "action" | "result" | "scope" | "agent" | "error" }
> =>
  row.kind === "action" ||
  row.kind === "result" ||
  row.kind === "scope" ||
  row.kind === "agent" ||
  row.kind === "error";

// "aud" or "audiences" offers to search only audiences, for every type a source can search.
const scopeRows = <A extends PaletteAction>(
  query: string,
  sources: CommandResultSource[],
): Array<PaletteRow<A>> => {
  if (query.length < MIN_SCOPE_QUERY_LENGTH) {
    return [];
  }
  const seen = new Set<string>();
  return sources.flatMap((source) =>
    (source.scopes ?? []).flatMap((scope): Array<PaletteRow<A>> => {
      const label = commandScopeLabel(scope);
      const start = scopeLabelMatchStart(label, query);
      if (seen.has(scope.type) || start === -1) {
        return [];
      }
      seen.add(scope.type);
      return [
        {
          kind: "scope",
          key: `scope:${scope.type}`,
          scope,
          label,
          match: {
            score: 1,
            ranges: [{ start, end: start + query.length }],
          },
        },
      ];
    }),
  );
};

export const buildRows = <A extends PaletteAction>({
  search,
  scope,
  rootActionId,
  matches,
  allActions,
  contextActionIds,
  contextObjectKey,
  sources,
  canAskAgent,
}: BuildRowsArgs<A>): Array<PaletteRow<A>> => {
  const query = search.trim();
  const toRow = (match: string | A): PaletteRow<A> =>
    typeof match === "string"
      ? { kind: "header", key: `header:${match}`, label: match }
      : {
          kind: "action",
          key: `action:${match.id}`,
          action: match,
          match: query ? matchName(match.name, query) : null,
        };
  const resultRow = (
    result: CommandResult,
    match: NameMatch | null = null,
  ): PaletteRow<A> => ({
    kind: "result",
    key: `result:${resultKey(result)}`,
    result,
    match,
  });

  if (rootActionId) {
    // kbar's matches can trail a page change by a render, so an empty query lists the page's children directly.
    if (!query && !matches.some((match) => isChildOf(match, rootActionId))) {
      return allActions
        .filter((action) => action.parent === rootActionId)
        .map(toRow);
    }
    return matches.map(toRow);
  }

  const seen = new Set<string>();
  const listedSources = sources.filter(
    (source) => query || scope || source.zeroQuery,
  );
  // One row stands for every failed source; Enter on it retries them all.
  const errorRows: Array<PaletteRow<A>> = listedSources.some(
    (source) => source.isError && !source.isLoading,
  )
    ? [{ kind: "error", key: "error", match: null }]
    : [];
  const collect = (list: CommandResultSource[]) =>
    list
      .flatMap((source) => source.results)
      .filter((result) => {
        const key = resultKey(result);
        const keep =
          !seen.has(key) &&
          key !== contextObjectKey &&
          (!scope || result.type === scope.type);
        seen.add(key);
        return keep;
      });
  const results = collect(listedSources.filter((source) => !source.deferred));
  const deferred = collect(listedSources.filter((source) => source.deferred));

  // A scope lists its command's children first, so Tab on Content opens its views above its files.
  const scopeCommands = scope
    ? allActions.filter((action) => action.parent === scope.type)
    : allActions;

  if (!query) {
    if (scope) {
      const items = [...results, ...deferred]
        .slice(0, MAX_SCOPED_ROWS)
        .map((r) => resultRow(r));
      return [
        ...scopeCommands.map(toRow),
        ...(scopeCommands.length && items.length
          ? [{ kind: "separator", key: "separator:scope" } as const]
          : []),
        ...items,
        ...errorRows,
      ];
    }
    // The root list comes from every action: kbar's matches trail the input, so they can still hold the last query's hits.
    const actions = inKbarOrder(
      allActions.filter(
        (action) => !action.parent && !action.id.startsWith(NESTED_ONLY_PREFIX),
      ),
    );
    const contextActions = contextActionIds.flatMap(
      (id) => actions.find((action) => action.id === id) ?? [],
    );
    const commands = actions.filter(
      (action) => !contextActionIds.includes(action.id),
    );
    // Separators, not headers, split page actions, recents and commands.
    return [
      contextActions.map(toRow),
      [
        ...results.slice(0, MAX_RECENT_ROWS).map((r) => resultRow(r)),
        ...errorRows,
      ],
      commands.map(toRow),
    ]
      .filter((group) => group.length)
      .flatMap((group, index) => [
        ...(index
          ? [{ kind: "separator", key: `separator:${index}` } as const]
          : []),
        ...group,
      ]);
  }

  const agent: Array<PaletteRow<A>> = canAskAgent
    ? [{ kind: "agent", key: "agent", query, match: null }]
    : [];
  const rankResults = (list: CommandResult[]) =>
    list.flatMap((result) => {
      const match = matchName(result.name, query);
      return match
        ? [{ row: resultRow(result, match), score: match.score }]
        : [];
    });
  const ranked = [
    ...scopeCommands.flatMap((action) => {
      if (action.id.startsWith(NESTED_ONLY_PREFIX)) {
        return [];
      }
      const match = matchName(action.name, query);
      const score =
        match?.score ??
        (matchName(action.keywords ?? "", query) ? KEYWORD_MATCH_SCORE : 0);
      const row: PaletteRow<A> = {
        kind: "action",
        key: `action:${action.id}`,
        action,
        match,
      };
      return score ? [{ row, score }] : [];
    }),
    ...rankResults(results),
  ];
  const byScore = (a: { score: number }, b: { score: number }) =>
    b.score - a.score;
  const late = rankResults(deferred)
    .sort(byScore)
    .map(({ row }) => row);
  if (scope) {
    return [
      ...[
        ...ranked
          .sort(byScore)
          .slice(0, MAX_SCOPED_ROWS)
          .map(({ row }) => row),
        ...late,
      ].slice(0, MAX_SCOPED_ROWS),
      ...errorRows,
      ...agent,
    ];
  }
  // A type's own command, whose id is the type ("library", "audiences"), stands for its scope: Enter opens the page, Tab searches it. Types without one keep their "Search …" row.
  const sorted = ranked.sort(byScore);
  const commandFor = (scope: CommandScope) =>
    sorted.find(
      ({ row }) => row.kind === "action" && row.action.id === scope.type,
    )?.row;
  const merged = new Set<PaletteRow<A>>();
  const scopes = scopeRows<A>(query, sources).map((row) => {
    const command = row.kind === "scope" ? commandFor(row.scope) : undefined;
    if (row.kind !== "scope" || command?.kind !== "action") {
      return row;
    }
    merged.add(command);
    return { ...command, scope: row.scope };
  });
  return [
    ...scopes,
    ...sorted
      .filter(({ row }) => !merged.has(row))
      .slice(0, MAX_ROWS - scopes.length - errorRows.length - agent.length)
      .map(({ row }) => row),
    ...late,
    ...errorRows,
    ...agent,
  ];
};
